import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LeagueCreationData, ValidationError } from '../../../types/league';

interface StepProps {
  data: Partial<LeagueCreationData>;
  updateData: (data: Partial<LeagueCreationData>) => void;
  errors: ValidationError[];
  addError: (field: string, message: string) => void;
  clearErrors: () => void;
}

const RulesStep: React.FC<StepProps> = ({ data, updateData, errors, addError, clearErrors }) => {
  const { t } = useTranslation();
  // Initialize handicap config with defaults if not set
  useEffect(() => {
    if (!data.handicapConfig) {
      updateData({
        handicapConfig: {
          base_average: 200,
          handicap_percentage: 80,
          calculation_frequency: 'per_league',
          lock_handicap_after_weeks: null,
          lock_starting_hdcp_for_weeks: null,
          a_max: null,
          b_max: null,
          c_max: null,
          d_max: null,
          e_max: null,
          league_max: null,
          bot_blind_average: null,
          bowler_blind_percentage: null,
          bowler_blind_include_handicap: false,
          handicap_type: 'individual',
          team_difference_percentage: null
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount - updateData is stable from parent
  const handleRulesChange = (description: string) => {
    updateData({
      rules: {
        description: description
      }
    });
    // Clear errors when user starts typing
    if (errors.some(e => e.field === 'rules.description')) {
      clearErrors();
    }
  };

  const handleHandicapConfigChange = (field: string, value: any) => {
    updateData({
      handicapConfig: {
        base_average: data.handicapConfig?.base_average,
        handicap_percentage: data.handicapConfig?.handicap_percentage,
        calculation_frequency: data.handicapConfig?.calculation_frequency,
        lock_handicap_after_weeks: data.handicapConfig?.lock_handicap_after_weeks,
        lock_starting_hdcp_for_weeks: data.handicapConfig?.lock_starting_hdcp_for_weeks,
        a_max: data.handicapConfig?.a_max,
        b_max: data.handicapConfig?.b_max,
        c_max: data.handicapConfig?.c_max,
        d_max: data.handicapConfig?.d_max,
        e_max: data.handicapConfig?.e_max,
        league_max: data.handicapConfig?.league_max,
        bot_blind_average: data.handicapConfig?.bot_blind_average,
        bowler_blind_percentage: data.handicapConfig?.bowler_blind_percentage,
        bowler_blind_include_handicap: data.handicapConfig?.bowler_blind_include_handicap,
        handicap_type: data.handicapConfig?.handicap_type,
        team_difference_percentage: data.handicapConfig?.team_difference_percentage,
        [field]: value
      }
    });
    // Clear errors when user modifies handicap config
    if (errors.some(e => e.field.startsWith('handicapConfig'))) {
      clearErrors();
    }
  };

  const getFieldError = (field: string) => {
    return errors.find(e => e.field === field)?.message;
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">{t('admin.createLeague.steps.rulesStep.title')}</h2>
        <p className="text-purple-200">
          {t('admin.createLeague.steps.rulesStep.subtitle')}
        </p>
      </div>

      {/* Handicap Configuration Section */}
      <div className="bg-white/5 rounded-lg p-6 border border-white/10">
        <h3 className="text-xl font-semibold text-white mb-4">⚙️ {t('admin.createLeague.steps.rulesStep.handicapConfig.sectionTitle')}</h3>
        <p className="text-purple-200 text-sm mb-6">
          {t('admin.createLeague.steps.rulesStep.handicapConfig.sectionDescription')}
        </p>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Handicap Type */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-purple-200 mb-2">
              {t('admin.createLeague.steps.rulesStep.handicapConfig.handicapType')} *
            </label>
            <select
              value={data.handicapConfig?.handicap_type || 'individual'}
              onChange={e => handleHandicapConfigChange('handicap_type', e.target.value)}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="individual" className="bg-gray-800">
                {t('admin.createLeague.steps.rulesStep.handicapConfig.typeIndividual')}
              </option>
              <option value="team_difference" className="bg-gray-800">
                {t('admin.createLeague.steps.rulesStep.handicapConfig.typeTeamDiff')}
              </option>
            </select>
            <p className="text-purple-300 text-sm mt-1">
              💡 {data.handicapConfig?.handicap_type === 'team_difference'
                ? t('admin.createLeague.steps.rulesStep.handicapConfig.typeTeamDiffHelp')
                : t('admin.createLeague.steps.rulesStep.handicapConfig.typeIndividualHelp')}
            </p>
          </div>
          {/* Team Difference Percentage */}
          {data.handicapConfig?.handicap_type === 'team_difference' && (
            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">
                {t('admin.createLeague.steps.rulesStep.handicapConfig.teamDiffPercentage')} *
              </label>
              <input
                type="number"
                min={1} max={100} step={1}
                value={data.handicapConfig?.team_difference_percentage ?? ''}
                onChange={e => handleHandicapConfigChange('team_difference_percentage', e.target.value ? Number(e.target.value) : null)}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="80"
              />
              <p className="text-purple-300 text-sm mt-1">
                💡 {t('admin.createLeague.steps.rulesStep.handicapConfig.teamDiffPercentageHelp')}
              </p>
            </div>
          )}
          {data.handicapConfig?.handicap_type !== 'team_difference' && (<>
          <div>
            <label htmlFor="baseAverage" className="block text-sm font-medium text-purple-200 mb-2">
              {t('admin.createLeague.steps.rulesStep.handicapConfig.baseAverage')} *
            </label>
            <input
              type="number"
              id="baseAverage"
              min="150"
              max="300"
              step="10"
              value={data.handicapConfig?.base_average || 200}
              onChange={(e) => handleHandicapConfigChange('base_average', parseInt(e.target.value))}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            {getFieldError('handicapConfig.base_average') && (
              <p className="text-red-400 text-sm mt-1">{getFieldError('handicapConfig.base_average')}</p>
            )}
            <p className="text-purple-300 text-sm mt-1">
              💡 {t('admin.createLeague.steps.rulesStep.handicapConfig.baseAverageHelp')}
            </p>
          </div>

          {/* Handicap Percentage */}
          <div>
            <label htmlFor="handicapPercentage" className="block text-sm font-medium text-purple-200 mb-2">
              {t('admin.createLeague.steps.rulesStep.handicapConfig.handicapPercentage')} *
            </label>
            <input
              type="number"
              id="handicapPercentage"
              min="50"
              max="100"
              step="5"
              value={data.handicapConfig?.handicap_percentage || 80}
              onChange={(e) => handleHandicapConfigChange('handicap_percentage', parseFloat(e.target.value))}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            {getFieldError('handicapConfig.handicap_percentage') && (
              <p className="text-red-400 text-sm mt-1">{getFieldError('handicapConfig.handicap_percentage')}</p>
            )}
            <p className="text-purple-300 text-sm mt-1">
              💡 {t('admin.createLeague.steps.rulesStep.handicapConfig.handicapPercentageHelp')}
            </p>
          </div>

          {/* Calculation Frequency */}
          <div>
            <label htmlFor="calculationFrequency" className="block text-sm font-medium text-purple-200 mb-2">
              {t('admin.createLeague.steps.rulesStep.handicapConfig.calculationFrequency')} *
            </label>
            <select
              id="calculationFrequency"
              value={data.handicapConfig?.calculation_frequency || 'per_league'}
              onChange={(e) => handleHandicapConfigChange('calculation_frequency', e.target.value)}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="per_league" className="bg-gray-800">{t('admin.createLeague.steps.rulesStep.handicapConfig.frequencies.perLeague')}</option>
              <option value="weekly" className="bg-gray-800">{t('admin.createLeague.steps.rulesStep.handicapConfig.frequencies.weekly')}</option>
              <option value="monthly" className="bg-gray-800">{t('admin.createLeague.steps.rulesStep.handicapConfig.frequencies.monthly')}</option>
              <option value="yearly" className="bg-gray-800">{t('admin.createLeague.steps.rulesStep.handicapConfig.frequencies.yearly')}</option>
            </select>
            {getFieldError('handicapConfig.calculation_frequency') && (
              <p className="text-red-400 text-sm mt-1">{getFieldError('handicapConfig.calculation_frequency')}</p>
            )}
            <p className="text-purple-300 text-sm mt-1">
              💡 {t('admin.createLeague.steps.rulesStep.handicapConfig.calculationFrequencyHelp')}
            </p>
          </div>

          {/* Lock Handicap After Weeks */}
          <div>
            <label htmlFor="lockHandicapAfterWeeks" className="block text-sm font-medium text-purple-200 mb-2">
              {t('admin.createLeague.steps.rulesStep.handicapConfig.lockHandicapAfterWeeks')}
            </label>
            <input
              type="number"
              id="lockHandicapAfterWeeks"
              min="1"
              max="52"
              placeholder={t('admin.createLeague.steps.rulesStep.handicapConfig.lockHandicapAfterWeeksPlaceholder')}
              value={data.handicapConfig?.lock_handicap_after_weeks || ''}
              onChange={(e) => handleHandicapConfigChange('lock_handicap_after_weeks', e.target.value ? parseInt(e.target.value) : null)}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            {getFieldError('handicapConfig.lock_handicap_after_weeks') && (
              <p className="text-red-400 text-sm mt-1">{getFieldError('handicapConfig.lock_handicap_after_weeks')}</p>
            )}
            <p className="text-purple-300 text-sm mt-1">
              💡 {t('admin.createLeague.steps.rulesStep.handicapConfig.lockHandicapAfterWeeksHelp')}
            </p>
          </div>

          {/* Lock Starting Handicap for Weeks */}
          <div>
            <label htmlFor="lockStartingHdcpWeeks" className="block text-sm font-medium text-purple-200 mb-2">
              {t('admin.createLeague.steps.rulesStep.handicapConfig.lockStartingHdcpWeeks')}
            </label>
            <input
              type="number"
              id="lockStartingHdcpWeeks"
              min="1"
              max="52"
              placeholder={t('admin.createLeague.steps.rulesStep.handicapConfig.lockStartingHdcpWeeksPlaceholder')}
              value={data.handicapConfig?.lock_starting_hdcp_for_weeks || ''}
              onChange={(e) => handleHandicapConfigChange('lock_starting_hdcp_for_weeks', e.target.value ? parseInt(e.target.value) : null)}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            {getFieldError('handicapConfig.lock_starting_hdcp_for_weeks') && (
              <p className="text-red-400 text-sm mt-1">{getFieldError('handicapConfig.lock_starting_hdcp_for_weeks')}</p>
            )}
            <p className="text-purple-300 text-sm mt-1">
              💡 {t('admin.createLeague.steps.rulesStep.handicapConfig.lockStartingHdcpWeeksHelp')}
            </p>
          </div>
          </>)}
        </div>

        {/* Maximum Handicap Caps — individual handicap only */}
        {data.handicapConfig?.handicap_type !== 'team_difference' && (
        <div className="mt-6">
          <h4 className="text-lg font-semibold text-white mb-4">🎯 {t('admin.createLeague.steps.rulesStep.caps.sectionTitle')}</h4>
          <p className="text-purple-200 text-sm mb-4">
            {t('admin.createLeague.steps.rulesStep.caps.sectionDescription')}
          </p>

          <div className="grid md:grid-cols-3 gap-4">
            {/* Division A Max */}
            <div>
              <label htmlFor="aMax" className="block text-sm font-medium text-purple-200 mb-2">
                {t('admin.createLeague.steps.rulesStep.caps.divisionAMax')}
              </label>
              <input
                type="number"
                id="aMax"
                min="0"
                max="100"
                placeholder={t('admin.createLeague.steps.rulesStep.caps.noLimit')}
                value={data.handicapConfig?.a_max || ''}
                onChange={(e) => handleHandicapConfigChange('a_max', e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* Division B Max */}
            <div>
              <label htmlFor="bMax" className="block text-sm font-medium text-purple-200 mb-2">
                {t('admin.createLeague.steps.rulesStep.caps.divisionBMax')}
              </label>
              <input
                type="number"
                id="bMax"
                min="0"
                max="100"
                placeholder={t('admin.createLeague.steps.rulesStep.caps.noLimit')}
                value={data.handicapConfig?.b_max || ''}
                onChange={(e) => handleHandicapConfigChange('b_max', e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* Division C Max */}
            <div>
              <label htmlFor="cMax" className="block text-sm font-medium text-purple-200 mb-2">
                {t('admin.createLeague.steps.rulesStep.caps.divisionCMax')}
              </label>
              <input
                type="number"
                id="cMax"
                min="0"
                max="100"
                placeholder={t('admin.createLeague.steps.rulesStep.caps.noLimit')}
                value={data.handicapConfig?.c_max || ''}
                onChange={(e) => handleHandicapConfigChange('c_max', e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* Division D Max */}
            <div>
              <label htmlFor="dMax" className="block text-sm font-medium text-purple-200 mb-2">
                {t('admin.createLeague.steps.rulesStep.caps.divisionDMax')}
              </label>
              <input
                type="number"
                id="dMax"
                min="0"
                max="100"
                placeholder={t('admin.createLeague.steps.rulesStep.caps.noLimit')}
                value={data.handicapConfig?.d_max || ''}
                onChange={(e) => handleHandicapConfigChange('d_max', e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* Division E Max */}
            <div>
              <label htmlFor="eMax" className="block text-sm font-medium text-purple-200 mb-2">
                {t('admin.createLeague.steps.rulesStep.caps.divisionEMax')}
              </label>
              <input
                type="number"
                id="eMax"
                min="0"
                max="100"
                placeholder={t('admin.createLeague.steps.rulesStep.caps.noLimit')}
                value={data.handicapConfig?.e_max || ''}
                onChange={(e) => handleHandicapConfigChange('e_max', e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* League-Wide Max */}
            <div>
              <label htmlFor="leagueMax" className="block text-sm font-medium text-purple-200 mb-2">
                {t('admin.createLeague.steps.rulesStep.caps.leagueWideMax')}
              </label>
              <input
                type="number"
                id="leagueMax"
                min="0"
                max="100"
                placeholder={t('admin.createLeague.steps.rulesStep.caps.noLimit')}
                value={data.handicapConfig?.league_max || ''}
                onChange={(e) => handleHandicapConfigChange('league_max', e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <p className="text-purple-300 text-sm mt-3">
            💡 {t('admin.createLeague.steps.rulesStep.caps.capsHelp')}
          </p>
        </div>
        )}

        {/* Blind Configuration */}
        <div className="mt-6">
          <h4 className="text-lg font-semibold text-white mb-4">👻 {t('admin.createLeague.steps.rulesStep.blindConfig.sectionTitle')}</h4>
          <p className="text-purple-200 text-sm mb-4">
            {t('admin.createLeague.steps.rulesStep.blindConfig.sectionDescription')}
          </p>

          <div className="grid md:grid-cols-3 gap-4">
            {/* Bot Blind Average */}
            <div>
              <label htmlFor="botBlindAverage" className="block text-sm font-medium text-purple-200 mb-2">
                {t('admin.createLeague.steps.rulesStep.blindConfig.botBlindAverage')}
              </label>
              <input
                type="number"
                id="botBlindAverage"
                min="100"
                max="250"
                placeholder={t('admin.createLeague.steps.rulesStep.blindConfig.optional')}
                value={data.handicapConfig?.bot_blind_average || ''}
                onChange={(e) => handleHandicapConfigChange('bot_blind_average', e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <p className="text-purple-300 text-sm mt-1">
                💡 {t('admin.createLeague.steps.rulesStep.blindConfig.botBlindAverageHelp')}
              </p>
            </div>

            {/* Bowler Blind Percentage */}
            <div>
              <label htmlFor="bowlerBlindPercentage" className="block text-sm font-medium text-purple-200 mb-2">
                {t('admin.createLeague.steps.rulesStep.blindConfig.bowlerBlindPercentage')}
              </label>
              <select
                id="bowlerBlindPercentage"
                value={data.handicapConfig?.bowler_blind_percentage || ''}
                onChange={(e) => handleHandicapConfigChange('bowler_blind_percentage', e.target.value ? parseFloat(e.target.value) : null)}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="" className="bg-gray-800">{t('admin.createLeague.steps.rulesStep.blindConfig.optional')}</option>
                <option value="50" className="bg-gray-800">50%</option>
                <option value="60" className="bg-gray-800">60%</option>
                <option value="70" className="bg-gray-800">70%</option>
                <option value="80" className="bg-gray-800">80%</option>
                <option value="90" className="bg-gray-800">90%</option>
                <option value="100" className="bg-gray-800">100%</option>
              </select>
              <p className="text-purple-300 text-sm mt-1">
                💡 {t('admin.createLeague.steps.rulesStep.blindConfig.bowlerBlindPercentageHelp')}
              </p>
            </div>

            {/* Bowler Blind Include Handicap */}
            <div>
              <label htmlFor="bowlerBlindIncludeHandicap" className="block text-sm font-medium text-purple-200 mb-2">
                {t('admin.createLeague.steps.rulesStep.blindConfig.bowlerBlindIncludeHandicap')}
              </label>
              <div className="flex items-center h-12 px-4 bg-white/10 border border-white/20 rounded-lg">
                <input
                  type="checkbox"
                  id="bowlerBlindIncludeHandicap"
                  checked={data.handicapConfig?.bowler_blind_include_handicap || false}
                  onChange={(e) => handleHandicapConfigChange('bowler_blind_include_handicap', e.target.checked)}
                  className="w-5 h-5 text-purple-600 bg-gray-700 border-gray-600 rounded focus:ring-purple-500 focus:ring-2"
                />
                <label htmlFor="bowlerBlindIncludeHandicap" className="ml-3 text-white">
                  {t('admin.createLeague.steps.rulesStep.blindConfig.bowlerBlindIncludeHandicapLabel')}
                </label>
              </div>
              <p className="text-purple-300 text-sm mt-1">
                💡 {t('admin.createLeague.steps.rulesStep.blindConfig.bowlerBlindIncludeHandicapHelp')}
              </p>
            </div>
          </div>
        </div>

        {/* Handicap Example Calculation */}
        <div className="mt-6 bg-purple-900/20 rounded-lg p-4 border border-purple-500/30">
          <h4 className="text-sm font-semibold text-purple-200 mb-2">📊 {t('admin.createLeague.steps.rulesStep.example.title')}</h4>
          <p className="text-purple-200 text-sm">
            {t('admin.createLeague.steps.rulesStep.example.description')}
          </p>
          <p className="text-purple-100 text-sm mt-1">
            {t('admin.createLeague.steps.rulesStep.example.formula', {
              baseAverage: data.handicapConfig?.base_average || 200,
              percentage: data.handicapConfig?.handicap_percentage || 80,
              result: Math.round((((data.handicapConfig?.base_average || 200) - 150) * ((data.handicapConfig?.handicap_percentage || 80) / 100)))
            })}
          </p>
        </div>
      </div>

      {/* Rules Section */}
      <div>
        <label htmlFor="rulesDescription" className="block text-sm font-medium text-purple-200 mb-2">
          {t('admin.createLeague.steps.rulesStep.rules.label')} *
        </label>
        <textarea
          id="rulesDescription"
          rows={10}
          value={data.rules?.description || ''}
          onChange={(e) => handleRulesChange(e.target.value)}
          className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          placeholder={t('admin.createLeague.steps.rulesStep.rules.placeholder')}
        />
        {getFieldError('rules.description') && (
          <p className="text-red-400 text-sm mt-1">{getFieldError('rules.description')}</p>
        )}
        <p className="text-purple-300 text-sm mt-2">
          💡 {t('admin.createLeague.steps.rulesStep.rules.tip')}
        </p>
      </div>

      {/* Rules Examples */}
      <div className="bg-white/5 rounded-lg p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-4">{t('admin.createLeague.steps.rulesStep.commonCategories.title')}</h3>
        <div className="grid md:grid-cols-2 gap-4 text-purple-200 text-sm">
          <div>
            <h4 className="font-semibold text-white mb-2">{t('admin.createLeague.steps.rulesStep.commonCategories.attendance.title')}</h4>
            <ul className="space-y-1 list-disc list-inside">
              <li>{t('admin.createLeague.steps.rulesStep.commonCategories.attendance.items.startTime')}</li>
              <li>{t('admin.createLeague.steps.rulesStep.commonCategories.attendance.items.lateArrival')}</li>
              <li>{t('admin.createLeague.steps.rulesStep.commonCategories.attendance.items.absenceNotifications')}</li>
              <li>{t('admin.createLeague.steps.rulesStep.commonCategories.attendance.items.substitutes')}</li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold text-white mb-2">{t('admin.createLeague.steps.rulesStep.commonCategories.payments.title')}</h4>
            <ul className="space-y-1 list-disc list-inside">
              <li>{t('admin.createLeague.steps.rulesStep.commonCategories.payments.items.dueDates')}</li>
              <li>{t('admin.createLeague.steps.rulesStep.commonCategories.payments.items.latePenalties')}</li>
              <li>{t('admin.createLeague.steps.rulesStep.commonCategories.payments.items.refunds')}</li>
              <li>{t('admin.createLeague.steps.rulesStep.commonCategories.payments.items.prizeFund')}</li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold text-white mb-2">{t('admin.createLeague.steps.rulesStep.commonCategories.conduct.title')}</h4>
            <ul className="space-y-1 list-disc list-inside">
              <li>{t('admin.createLeague.steps.rulesStep.commonCategories.conduct.items.sportsmanship')}</li>
              <li>{t('admin.createLeague.steps.rulesStep.commonCategories.conduct.items.language')}</li>
              <li>{t('admin.createLeague.steps.rulesStep.commonCategories.conduct.items.alcoholSmoking')}</li>
              <li>{t('admin.createLeague.steps.rulesStep.commonCategories.conduct.items.disputes')}</li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold text-white mb-2">{t('admin.createLeague.steps.rulesStep.commonCategories.equipment.title')}</h4>
            <ul className="space-y-1 list-disc list-inside">
              <li>{t('admin.createLeague.steps.rulesStep.commonCategories.equipment.items.approved')}</li>
              <li>{t('admin.createLeague.steps.rulesStep.commonCategories.equipment.items.dressCode')}</li>
              <li>{t('admin.createLeague.steps.rulesStep.commonCategories.equipment.items.laneCourtesy')}</li>
              <li>{t('admin.createLeague.steps.rulesStep.commonCategories.equipment.items.safety')}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RulesStep;